import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Card,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
  daysUntil,
  formatDate,
  formatDateTime,
} from '@asad/crane-ui'

import { alertStatusOf, inspectionToneClass, resourceBarClass } from '../config/statusConfig'
import type { DeviceStatusType, FleetDeviceEvent, MapDevice } from '../types'
import { DeviceTypeIcon } from './DeviceTypeIcon'
import { PowerChip, StatusChip } from './StatusChip'

const isErrorEvent = (event: FleetDeviceEvent): boolean => Boolean(event.inverterId)

const eventBgClass = (event: FleetDeviceEvent): string =>
  isErrorEvent(event)
    ? 'border-l-crane-status-error bg-crane-status-error/10'
    : 'border-l-crane-status-alarm bg-crane-status-alarm/10'

const eventChipClass = (event: FleetDeviceEvent): string =>
  isErrorEvent(event)
    ? 'bg-crane-status-error/20 text-crane-status-error'
    : 'bg-crane-status-alarm/20 text-crane-status-alarm'

const eventChipHoverClass = (event: FleetDeviceEvent): string =>
  isErrorEvent(event)
    ? 'hover:bg-crane-status-error/30 hover:text-crane-status-error'
    : 'hover:bg-crane-status-alarm/30 hover:text-crane-status-alarm'

export type FleetDeviceCardProps = {
  device: MapDevice
  deviceStatus?: DeviceStatusType
  hasActiveBorder?: boolean
  locationLabel?: string
  activeEvents?: FleetDeviceEvent[]
  eventLabel?: (event: FleetDeviceEvent) => string
  onSelect?: (deviceId: string) => void
  compact?: boolean
  interactive?: boolean
}

/** Karta monitoringu urządzenia z niezależnymi stanami pracy i alarmów. */
const FleetDeviceCardComponent = ({
  device,
  deviceStatus,
  hasActiveBorder = false,
  locationLabel,
  activeEvents = [],
  eventLabel,
  onSelect,
  compact = false,
  interactive = true,
}: FleetDeviceCardProps) => {
  const { t, i18n } = useTranslation()

  const alertStatus = alertStatusOf(deviceStatus)
  const isWorking = deviceStatus?.isWorking ?? 0
  const resource = deviceStatus?.resource ?? device.resource ?? 0
  const resourceLabel = device.resourceLabel ?? t('device.resource')
  const inspection = device.inspections?.[0]
  const days = inspection ? daysUntil(inspection.validityDate) : null
  const topEvent = activeEvents[0]
  const remainingEvents = activeEvents.slice(1)

  const handleSelect = () => onSelect?.(device.id)

  const inspectionText = () => {
    if (!inspection || days === null) return t('common.noInspection')
    if (days < 0) return t('common.inspectionExpired', { daysAgo: -days })

    return t('common.daysUntilExpirationUdt', { daysUntilExpirationUdt: days })
  }

  return (
    <Card
      className={cn(
        'relative flex h-full min-w-0 flex-col rounded-crane-md outline-none ring-1 ring-inset ring-crane-border/40',
        compact ? 'gap-2 p-3' : 'gap-3 p-4',
        interactive &&
          'cursor-pointer transition-crane-lift hover:-translate-y-px hover:shadow-crane-soft-lg hover:ring-crane-border/80 focus-visible:ring-2 focus-visible:ring-crane-ring',
        hasActiveBorder && 'shadow-crane-soft-lg ring-2 ring-crane-primary/60',
      )}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? handleSelect : undefined}
      onKeyDown={(event) => {
        if (interactive && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault()
          handleSelect()
        }
      }}
    >
      <div className='flex h-crane-avatar shrink-0 items-start gap-2.5'>
        <span className='flex size-crane-avatar shrink-0 items-center justify-center rounded-crane-sm bg-crane-card-inset text-crane-muted-foreground'>
          <DeviceTypeIcon className='size-crane-icon-lg' />
        </span>
        <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
          <div className='truncate text-crane-base font-semibold leading-5 text-crane-foreground' title={device.name}>
            {device.name}
          </div>
          {locationLabel ? (
            <div
              className='truncate text-crane-sm leading-crane-card-meta text-crane-muted-foreground'
              title={locationLabel}
            >
              {locationLabel}
            </div>
          ) : null}
        </div>
      </div>

      <div className='flex h-6 shrink-0 items-center gap-1.5 overflow-hidden'>
        <PowerChip isWorking={isWorking} />
        <StatusChip alertStatus={alertStatus} variant='solid' withIcon />
      </div>

      {topEvent ? (
        <div
          className={cn(
            'flex min-w-0 flex-wrap items-center gap-2 rounded-crane-sm border-0 border-l-crane-emphasis px-2.5 py-1.5',
            eventBgClass(topEvent),
          )}
        >
          <Badge
            variant='outline'
            className={cn(
              'shrink-0 border-transparent px-1.5 py-px text-crane-2xs font-extrabold tracking-wide',
              eventChipClass(topEvent),
            )}
          >
            {topEvent.value}
          </Badge>
          <span className='min-w-0 flex-1 truncate text-xs text-crane-foreground' title={eventLabel?.(topEvent)}>
            {eventLabel?.(topEvent)}
          </span>

          {remainingEvents.length > 0 ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  title={t('device.showAllEvents')}
                  className={cn(
                    'h-auto shrink-0 rounded-crane-sm px-2.5 py-0.5 text-crane-xs font-bold',
                    eventChipClass(topEvent),
                    eventChipHoverClass(topEvent),
                  )}
                  onClick={(event) => event.stopPropagation()}
                >
                  +{remainingEvents.length}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align='end'
                className='flex max-h-crane-popover-list w-crane-panel-wide flex-col gap-1 overflow-auto p-2'
                onClick={(event) => event.stopPropagation()}
              >
                <span className='px-1 pb-1 pt-0.5 text-crane-xs font-bold text-crane-muted-foreground'>
                  {t('device.activeEvents')} ({activeEvents.length})
                </span>
                {activeEvents.map((event) => (
                  <Button
                    key={event.id}
                    type='button'
                    variant='ghost'
                    className={cn(
                      'h-auto flex-col items-stretch justify-start gap-0.5 rounded-crane-lg border-0 border-l-2 bg-crane-card px-2 py-1.5 text-left font-normal hover:bg-crane-card-elevated',
                      eventBgClass(event),
                    )}
                    onClick={(clickEvent) => {
                      clickEvent.stopPropagation()
                      handleSelect()
                    }}
                  >
                    <span
                      className={cn(
                        'self-start rounded-crane-md px-1.5 py-px text-crane-2xs font-extrabold tracking-wide',
                        eventChipClass(event),
                      )}
                    >
                      {isErrorEvent(event) ? t('device.error') : t('device.alarm')} {event.value}
                    </span>
                    <span className='text-xs leading-crane-event text-crane-foreground'>{eventLabel?.(event)}</span>
                    <span className='text-crane-2xs text-crane-muted-foreground'>
                      {formatDateTime(event.createdAt, i18n.language)}
                    </span>
                  </Button>
                ))}
              </PopoverContent>
            </Popover>
          ) : null}
        </div>
      ) : null}

      <div className='flex h-7 shrink-0 flex-col justify-between'>
        <div className='flex items-baseline justify-between gap-2.5'>
          <span className='truncate text-xs text-crane-muted-foreground'>{resourceLabel}</span>
          <span className='shrink-0 whitespace-nowrap text-xs font-bold text-crane-foreground'>{resource}%</span>
        </div>
        <div className='h-1.5 overflow-hidden rounded-crane-sm bg-crane-card-inset'>
          <div
            className={cn('h-full rounded-crane-sm', resourceBarClass(resource))}
            style={{ width: `${resource}%` }}
          />
        </div>
      </div>

      <div
        className={cn(
          'flex min-w-0 shrink-0 flex-col border-t border-crane-border/40 pt-2.5',
          compact ? 'gap-2' : 'gap-3',
        )}
      >
        <div className='flex h-crane-chip items-center'>
          <span className='truncate text-crane-sm text-crane-muted-foreground'>
            {t('device.serialNumber')}: {device.serialNumber} · {t('device.udtNumber')}: {device.udtNumber}
          </span>
        </div>

        <div className='flex h-crane-chip items-center'>
          <Badge
            variant='outline'
            className={cn(
              'h-full max-w-full overflow-hidden whitespace-nowrap rounded-crane-sm border-transparent bg-crane-card-inset px-2.5 py-0 text-crane-xs font-bold',
              inspectionToneClass(days, inspection?.warningDays),
            )}
            title={
              inspection?.inspectionDate
                ? `${t('common.inspectionDate')}: ${formatDate(inspection.inspectionDate, i18n.language)}`
                : t('common.noInspection')
            }
          >
            <span className='truncate'>{inspectionText()}</span>
          </Badge>
        </div>

        <div className='flex h-8 shrink-0 items-center'>
          {interactive ? (
            <Button
              type='button'
              variant='secondary'
              size='sm'
              className='h-full w-full rounded-crane-sm'
              onClick={(event) => {
                event.stopPropagation()
                handleSelect()
              }}
            >
              {t('commonActions.details')}
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  )
}

const areFleetDeviceCardPropsEqual = (previous: FleetDeviceCardProps, next: FleetDeviceCardProps): boolean => {
  const previousInspection = previous.device.inspections?.[0]
  const nextInspection = next.device.inspections?.[0]

  return (
    previous.device.id === next.device.id &&
    previous.device.name === next.device.name &&
    previous.device.serialNumber === next.device.serialNumber &&
    previous.device.udtNumber === next.device.udtNumber &&
    previous.device.resource === next.device.resource &&
    previous.device.resourceLabel === next.device.resourceLabel &&
    previousInspection?.validityDate === nextInspection?.validityDate &&
    previousInspection?.warningDays === nextInspection?.warningDays &&
    previousInspection?.inspectionDate === nextInspection?.inspectionDate &&
    previous.deviceStatus?.alertStatus === next.deviceStatus?.alertStatus &&
    previous.deviceStatus?.isWorking === next.deviceStatus?.isWorking &&
    previous.deviceStatus?.resource === next.deviceStatus?.resource &&
    previous.hasActiveBorder === next.hasActiveBorder &&
    previous.locationLabel === next.locationLabel &&
    previous.activeEvents === next.activeEvents &&
    previous.eventLabel === next.eventLabel &&
    previous.onSelect === next.onSelect &&
    previous.compact === next.compact &&
    previous.interactive === next.interactive
  )
}

export const FleetDeviceCard = React.memo(FleetDeviceCardComponent, areFleetDeviceCardPropsEqual)
FleetDeviceCard.displayName = 'FleetDeviceCard'
